import {
  ComponentProps,
  forwardRef,
  useImperativeHandle,
  useRef,
  ChangeEvent,
  PropsWithChildren,
  ReactNode,
} from 'react';

import cx from 'classnames';

import { Input as ShadcnInput } from '../shadcn/Input';
import { renderMessages, TCommonInputProps } from './useInputLogic';
import { TInputBaseOnChange } from './InputTypes';

// Tailwind classes for statuses (replace SCSS)
const STATUS_CLASSES: Record<string, string> = {
  error: 'border-destructive focus-visible:ring-destructive',
  success: 'border-success focus-visible:ring-success',
  warning: 'border-warning focus-visible:ring-warning',
  info: 'border-info focus-visible:ring-info',
};

// Tailwind classes for sizes
const SIZE_CLASSES: Record<string, string> = {
  xs: 'h-8 px-2 text-xs',
  s: 'h-9 px-3 text-sm',
  m: 'h-10 px-3 text-sm',
  l: 'h-11 px-4 text-base',
  xl: 'h-12 px-5 text-lg',
};

export type TInputTextProps = TCommonInputProps & {
  children?: ReactNode;
  onChange?: (value: string, event?: ChangeEvent<HTMLInputElement>) => void;
  type?: 'text' | 'password' | 'email' | 'number';
};

export const InputText = forwardRef<
  HTMLInputElement,
  PropsWithChildren<TInputTextProps>
>(function InputText(props: TInputTextProps, ref?) {
  const {
    classes,
    className = '',
    messages,
    status,
    value = '',
    onChange,
    isMessagesRemoved,
    isHidden,
    disabled,
    readOnly,
    maxLength,
    type = 'text',
    size,
    allowedPattern,
    children,
    backgroundColor,
    textColor,
    // Exclude unused props for backward compatibility
    ...rest
  } = props;

  const inputProps = {
    ...rest,
  } as Record<string, unknown> & ComponentProps<typeof ShadcnInput>;

  delete inputProps.color;
  delete inputProps.isHovered;
  delete inputProps.tooltipStatus;
  delete inputProps.variant;

  const inputRef = useRef<HTMLInputElement>(null);
  useImperativeHandle(ref, () => inputRef.current!);

  // Determine the status to display
  const displayStatus = isMessagesRemoved
    ? status
    : messages
      ? status
      : undefined;

  // Change handler
  const handleChange: TInputBaseOnChange = (newValue, event) => {
    // allowedPattern filtering
    if (allowedPattern) {
      newValue = Array.from(newValue)
        .filter((char: string) => allowedPattern.test(char))
        .join('');
    }
    onChange?.(newValue, event);
  };

  // CSS classes
  const containerClassName = cx(
    'flex flex-col gap-1',
    className,
    isHidden && 'visually-hidden',
  );

  const inputClassName = cx(
    SIZE_CLASSES[size || 'm'],
    displayStatus && STATUS_CLASSES[displayStatus],
    classes?.input?.base,
    displayStatus && classes?.input?.[displayStatus],
  );

  const renderMessagesComponent = renderMessages(
    {
      messages,
      classes,
      status,
      maxLength,
      valueLength: value?.length || 0,
    },
    false,
  );

  // If there are children (e.g. show password button), render with relative
  const inputWrapper = children ? (
    <div className="relative">
      <ShadcnInput
        ref={inputRef}
        type={type}
        value={value}
        disabled={disabled}
        readOnly={readOnly}
        maxLength={maxLength}
        className={cx(inputClassName, 'pr-10')}
        onChange={(e) => handleChange(e.target.value, e)}
        backgroundColor={backgroundColor}
        textColor={textColor}
        {...inputProps}
      />
      <div className="absolute right-3 top-1/2 -translate-y-1/2">
        {children}
      </div>
    </div>
  ) : (
    <ShadcnInput
      ref={inputRef}
      type={type}
      value={value}
      disabled={disabled}
      readOnly={readOnly}
      maxLength={maxLength}
      className={inputClassName}
      onChange={(e) => handleChange(e.target.value, e)}
      backgroundColor={backgroundColor}
      textColor={textColor}
      {...inputProps}
    />
  );

  return (
    <div className={containerClassName}>
      {inputWrapper}
      {!isMessagesRemoved && renderMessagesComponent}
    </div>
  );
});
